import { useState, useRef, useEffect, useCallback, startTransition } from "react";
import { Menu, X, ChevronDown, Code, Cloud, Database, Rocket, GraduationCap } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsMenuOpen, setIsSolutionsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Deferred scroll listener - doesn't block TTI
  useEffect(() => {
    // Check initial state synchronously (non-blocking read)
    const initialScrolled = window.scrollY > 0;
    if (initialScrolled) setIsScrolled(true);
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          // Use startTransition for non-urgent UI update
          startTransition(() => {
            setIsScrolled(window.scrollY > 0);
          });
          ticking = false;
        });
      }
    };

    // Passive listener for better scroll performance
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const solutionsDropdownRef = useRef<HTMLDivElement>(null);
  const solutionPages = [{
    label: "MVP Development",
    href: "/services/mvp-development",
    icon: Rocket,
    iconColor: "bg-orange-500",
    description: "Launch in 2-3 weeks"
  }, {
    label: "Custom Development",
    href: "/services/custom-development",
    icon: Code,
    iconColor: "bg-blue-500",
    description: "Web, Mobile & AI solutions"
  }, {
    label: "Cloud Solutions",
    href: "/services/cloud-solutions",
    icon: Cloud,
    iconColor: "bg-green-500",
    description: "Scalable cloud infrastructure"
  }, {
    label: "Data Visualisation",
    href: "/services/data-visualisation",
    icon: Database,
    iconColor: "bg-indigo-500",
    description: "Data pipelines & analytics"
  }, {
    label: "Internships",
    href: "/internship",
    icon: GraduationCap,
    iconColor: "bg-purple-500",
    description: "Learn & grow with us"
  }];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (solutionsDropdownRef.current && !solutionsDropdownRef.current.contains(event.target as Node)) {
        setIsSolutionsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleAboutClick = () => {
    navigate("/about");
    setIsMenuOpen(false);
  };
  const scrollToContact = () => {
    if (window.location.pathname === "/") {
      const element = document.querySelector("#contact");
      if (element) {
        element.scrollIntoView({
          behavior: "smooth"
        });
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector("#contact");
        if (element) {
          element.scrollIntoView({
            behavior: "smooth"
          });
        }
      }, 100);
    }
  };
  const closeAllDropdowns = () => {
    setIsSolutionsMenuOpen(false);
  };
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 bg-white/40 backdrop-blur-2xl ${isScrolled ? "border-b border-white/20 shadow-xl shadow-black/10" : "border-b border-white/10"}`} style={{
      backdropFilter: "blur(60px)",
      WebkitBackdropFilter: "blur(60px)"
    }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center h-14 sm:h-16 relative">
            {/* Mobile Menu Button - positioned absolute left */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden absolute left-0 p-2 rounded-lg hover:bg-gray-100 transition-colors z-[60]" aria-label="Toggle menu">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Centered Navigation Items */}
            <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-6 lg:space-x-8">
              {/* Logo */}
              <button onClick={() => navigate("/")} className="flex items-center gap-3 hover-scale transition-transform duration-200">
                <img src="/lovable-uploads/ITPL Logo.webp" alt="Interesting Technologies Logo" className="h-8 sm:h-9 md:h-10 w-auto rounded-xl" width="441" height="145" />
              </button>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-3 lg:space-x-6">
                <button onClick={handleAboutClick} className="text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 relative group text-sm lg:text-base">
                  About
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                </button>

                {/* Our Solutions Dropdown */}
                <div className="relative" ref={solutionsDropdownRef}>
                  <button className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 text-sm lg:text-base" onMouseEnter={() => {
                  closeAllDropdowns();
                  setIsSolutionsMenuOpen(true);
                }} onClick={() => setIsSolutionsMenuOpen(!isSolutionsMenuOpen)}>
                    Services
                    <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4" />
                  </button>

                  {isSolutionsMenuOpen && <div className="absolute top-full left-0 mt-2 w-[520px] rounded-2xl shadow-2xl shadow-blue-500/20 border border-gray-200 py-6 z-50 bg-white" style={{}} onMouseLeave={() => setIsSolutionsMenuOpen(false)}>
                      <div className="grid grid-cols-2 gap-2 px-4">
                        {solutionPages.map(solution => {
                      const IconComponent = solution.icon;
                      return <Link key={solution.label} to={solution.href} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-all duration-300 group" onClick={() => setIsSolutionsMenuOpen(false)}>
                              <div className={`w-10 h-10 ${solution.iconColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                <IconComponent className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-gray-900 font-semibold text-sm group-hover:text-blue-600 transition-colors">
                                  {solution.label}
                                </span>
                                <span className="text-gray-500 text-xs mt-0.5">{solution.description}</span>
                              </div>
                            </Link>;
                    })}
                      </div>
                    </div>}
                </div>

                {/* Our Work Link */}
                <Link to="/our-work" className="text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 relative group text-sm lg:text-base">
                  Products
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                </Link>

                <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 relative group text-sm lg:text-base">
                  Contact
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {isMenuOpen && <div className="md:hidden fixed inset-0 z-[55]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/20" onClick={() => setIsMenuOpen(false)} />

          {/* Menu Content */}
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto animate-slide-in-right" style={{
        willChange: "transform, opacity"
      }}>
            <div className="px-6 py-6">
              <div className="flex flex-col space-y-1">
                <button onClick={handleAboutClick} className="text-center text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-all duration-300 text-lg">
                  About
                </button>

                <div className="border-t border-gray-100 pt-4 mt-4">
                  <span className="block text-gray-900 mb-3 px-4 uppercase tracking-wide text-center text-lg font-medium">
                    Our Solutions
                  </span>
                  <div className="space-y-1">
                    {solutionPages.map(solution => <Link key={solution.label} to={solution.href} className="block text-gray-600 hover:text-blue-600 py-2 px-6 rounded-lg hover:bg-gray-50 transition-all duration-300 text-sm text-center" onClick={() => setIsMenuOpen(false)}>
                        {solution.label}
                      </Link>)}
                  </div>
                </div>

                <Link to="/our-work" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-all duration-300 text-center block text-lg">
                  Our Products
                </Link>


                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-all duration-300 text-center block text-lg border-t border-gray-100 mt-4 pt-4">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>}
    </>;
};
export default Navigation;